import { useState } from 'react';
import axios from 'axios';
import { Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { BulkImportInfo, BulkImportResult } from './BulkImportInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BulkImportDialog({ open, onClose, creds, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a .csv file');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/products/bulk-template`, {
        auth: creds,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('Could not download template');
    }
  };

  const handleImport = async () => {
    if (!file) { toast.error('Choose a CSV file first'); return; }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/products/bulk-import`, fd, { auth: creds });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} product${r.data.created === 1 ? '' : 's'}`);
      if (r.data.created > 0) onImported?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
          : e?.message || 'Bulk import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bulk-import-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Products</DialogTitle>
          <DialogDescription>Upload a CSV to create many products at once.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <BulkImportInfo onDownloadTemplate={downloadTemplate} />
          <FilePicker file={file} onFile={onFile} />
          <BulkImportResult result={result} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>Close</Button>
          <Button
            data-testid="bulk-import-submit"
            onClick={handleImport}
            disabled={!file || importing}
            className="bg-[#01195d]"
          >
            {importing
              ? <><span className="w-4 h-4 mr-2 border-2 border-white/60 border-t-white rounded-full animate-spin" />Importing…</>
              : <><Upload className="w-4 h-4 mr-2" />Import</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilePicker({ file, onFile }) {
  return (
    <div>
      <Label>CSV file</Label>
      <label className="mt-1.5 flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-slate-400">
        <FilePickerContent file={file} />
        <input
          data-testid="bulk-import-file-input"
          type="file"
          accept=".csv"
          className="hidden"
          onChange={onFile}
        />
      </label>
    </div>
  );
}

function FilePickerContent({ file }) {
  return (
    <div className="text-center">
      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-1" />
      <span className="text-sm text-slate-600 font-medium">
        {file ? file.name : 'Choose CSV file'}
      </span>
      {file && <p className="text-xs text-slate-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>}
    </div>
  );
}

export default BulkImportDialog;
